import * as yup from 'yup';
import { Tag, Trash2, Users } from 'react-feather';
import { TypedDocumentNode } from '@apollo/client';
import { addDays, endOfYear, getYear, isSameDay, startOfYear, subYears } from 'date-fns';
import { useForm } from 'react-form-state-manager';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Reference from 'yup/lib/Reference';

import { AssignmentType, Gender, GetTicketQuery as GetTicket } from '__generated__/graphql';

import { getServerErrors } from '../../../../common/helpers';
import GetEventLayoutQuery from '../GetEventLayoutQuery';
import UI from '../../../../common/UI';
import useEditTicketMutation from './useEditTicketMutation';
import useIntegerInput from '../../../../common/useIntegerInput';
import useLocale from '../../../../common/useLocale';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

type Event = GetTicket['event'];
type Ticket = GetTicket['ticket'];

const editTicketRestrictionsSchema = yup.object({
  restrictions: yup.array(yup.object({
    min_age: yup.number().nullable(),
    max_age: yup.number().nullable().min(yup.ref('min_age') as Reference<number | null>),
  })),
});

export interface EditTicketRestrictionsFormProps {
  ticket: Ticket;
  event: Event
  refetchQueries?: TypedDocumentNode[];
}

const EditTicketRestrictionsForm = ({
  ticket, event, refetchQueries = [GetEventLayoutQuery],
}: EditTicketRestrictionsFormProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate, formatNumber } = useLocale();
  const project = useProject();
  const integerInput = useIntegerInput();
  const integerHandler = integerInput();

  const getValues = (ticket: Ticket) => ({
    capacity: ticket.capacity,
    max_per_order: ticket.max_per_order,
    restrictions: ticket.restrictions.map((restriction) => ({
      gender: restriction.gender,
      min_age: restriction.min_age,
      max_age: restriction.max_age,
    })),
    assignment_type: ticket.assignment_type,
  });

  const form = useForm({
    values: getValues(ticket),
  });

  useNavigationPrompt(form.changed());

  const [editTicket, { error, loading }] = useEditTicketMutation({
    input: {
      id: ticket.id,
      restrictions: form.values,
    },
    onSuccess: (ticket) => {
      form.reset(getValues(ticket));
    },
    refetchQueries,
  });

  const { valid, errors } = useValidation({
    schema: editTicketRestrictionsSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'restrictions'),
  });

  const restrictionKeys = useRef(form.values.restrictions.map((value, index) => index));

  const addRestriction = () => {
    form.append('restrictions', {
      gender: null,
      min_age: null,
      max_age: null,
    });
    restrictionKeys.current.push(Math.max(0, ...restrictionKeys.current) + 1);
  };

  const deleteRestriction = (index: number) => {
    form.delete('restrictions', index);
    restrictionKeys.current.splice(index, 1);
  };

  const getMinDateOfBirth = (age: number) => {
    const thresholdDate = parseDate(event.age_threshold_date, { format: 'internal_date' });

    return addDays(subYears(thresholdDate, age + 1), 1);
  };

  const getMaxDateOfBirth = (age: number) => {
    const thresholdDate = parseDate(event.age_threshold_date, { format: 'internal_date' });

    return subYears(thresholdDate, age);
  };

  const getDateOfBirthRange = (minAge: number | null, maxAge: number | null) => {
    const min = maxAge ? getMinDateOfBirth(maxAge) : null;
    const max = minAge ? getMaxDateOfBirth(minAge) : null;

    const minIsStartOfYear = min && isSameDay(startOfYear(min), min);
    const maxIsEndOfYear = max && isSameDay(endOfYear(max), max);

    const formattedMin = min && (
      minIsStartOfYear && (!max || maxIsEndOfYear) ? getYear(min) : formatDate(min, { format: 'display_compact_date' })
    );

    const formattedMax = max && (
      (!min || minIsStartOfYear) && maxIsEndOfYear ? getYear(max) : formatDate(max, { format: 'display_compact_date' })
    );

    if (formattedMin && formattedMax) {
      return `${formattedMin} – ${formattedMax}`;
    }

    if (formattedMin) {
      return t('date_or_later', { date: formattedMin });
    }

    if (formattedMax) {
      return t('date_or_earlier', { date: formattedMax });
    }

    return '';
  };

  const setAssignmentType = (val: AssignmentType) => {
    form.set((values) => ({
      ...values,
      assignment_type: val,
    }));
  };

  return (
    <UI.Form onSubmit={() => editTicket()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend>
            {t('capacity')}
          </UI.Legend>
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors.capacity, touched: form.touched.capacity }}>
          <UI.InputLabel htmlFor="max_number_registrations">
            {t('max_number_registrations')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('ticket_capacity_description')}
          </UI.InputDescription>
          <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
            <UI.Div>
              <UI.InputWrapper>
                <UI.DebouncedInput
                  {...form.text('capacity', { inputHandler: integerHandler })}
                  id="max_number_registrations"
                  format={integerHandler.format}
                  maxLength={10}
                  inputMode="numeric"
                  match={/[\d]/g}
                  placeholder="∞"
                />
                {ticket.units > 1 && form.values.capacity > 0 && (
                  <UI.Span>
                    <UI.Span sc={{ muted: true, small: true }}>
                      {'= '}
                      <UI.Icon>
                        <Tag />
                      </UI.Icon>
                      {' '}
                      {t('n_tickets', {
                        count: Math.floor(form.values.capacity / ticket.units),
                        value: formatNumber(Math.floor(form.values.capacity / ticket.units)),
                      })}
                    </UI.Span>
                  </UI.Span>
                )}
              </UI.InputWrapper>
              <UI.ErrorMessages attribute={t('capacity')} errors={errors.capacity} />
            </UI.Div>
          </UI.FormGrid>
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.max_per_order, touched: form.touched.max_per_order }}>
          <UI.InputLabel htmlFor="max_per_order">
            {t('max_per_order')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('ticket_max_per_order_description')}
          </UI.InputDescription>
          <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
            <UI.Div>
              <UI.InputWrapper>
                <UI.DebouncedInput
                  {...form.text('max_per_order', { inputHandler: integerInput() })}
                  format={integerInput().format}
                  id="max_per_order"
                  maxLength={3}
                  inputMode="numeric"
                  match={/[\d]/g}
                  placeholder="∞"
                />
                {ticket.units > 1 && form.values.max_per_order > 0 && (
                  <UI.Span>
                    <UI.Span sc={{ muted: true, small: true }}>
                      {'= '}
                      <UI.Icon>
                        <Users />
                      </UI.Icon>
                      {' '}
                      {t('n_registrations', {
                        count: ticket.units * form.values.max_per_order,
                        value: formatNumber(ticket.units * form.values.max_per_order),
                      })}
                    </UI.Span>
                  </UI.Span>
                )}
              </UI.InputWrapper>
              <UI.ErrorMessages attribute={t('max_per_order')} errors={errors.max_per_order} />
            </UI.Div>
          </UI.FormGrid>
        </UI.InputGroup>

        <UI.HR />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('gender_and_age_restrictions')}
          </UI.Legend>
          <UI.InputDescription>
            {t('gender_and_age_restrictions_description')}
          </UI.InputDescription>
        </UI.Div>

        {form.values.restrictions.length > 0 && (
          <UI.Info>
            {t('age_threshold_date_description', {
              date: formatDate(
                parseDate(event.age_threshold_date, { format: 'internal_date' }),
                { format: 'display_compact_date' },
              ),
            })}
            {' '}
            <UI.Link to={`/${project.id}/events/${event.id}/settings/restrictions`}>
              {t('configure')}
            </UI.Link>
          </UI.Info>
        )}

        <UI.FormGrid sc={{ gutter: 0.5 }}>
          {form.values.restrictions.length > 0 && (
            <>
              <UI.FormGrid sc={{ columns: '1fr 30px', gutter: 0.5 }}>
                <UI.FormGrid sc={{ columns: ['1fr', '2fr 2fr 2fr 3fr'], gutter: 0.5 }}>
                  <UI.InputLabel htmlFor="gender_0" sc={{ my: 0 }}>
                    {t('gender')}
                  </UI.InputLabel>
                  <UI.InputLabel htmlFor="min_age_0" sc={{ my: 0 }}>
                    {t('min_age')}
                  </UI.InputLabel>
                  <UI.InputLabel htmlFor="max_age_0" sc={{ my: 0 }}>
                    {t('max_age')}
                  </UI.InputLabel>
                </UI.FormGrid>
              </UI.FormGrid>
              {form.values.restrictions.map((restriction, index) => (
                <UI.FormGrid sc={{ columns: '1fr 30px', gutter: 0.5 }} key={restrictionKeys.current[index]}>
                  <UI.FormGrid sc={{ columns: ['1fr', '2fr 2fr 2fr 3fr'], gutter: 0.5 }}>
                    <UI.InputGroup>
                      <UI.Select
                        {...form.select(
                          `restrictions.${index}.gender`,
                          [null, Gender.male, Gender.female, Gender.neutral],
                        )}
                        id={`gender_${index}`}
                      >
                        <option value={0}>
                          {t('all')}
                        </option>
                        <option value={1}>
                          {t('common:genders.male')}
                        </option>
                        <option value={2}>
                          {t('common:genders.female')}
                        </option>
                        <option value={3}>
                          {t('common:genders.neutral')}
                        </option>
                      </UI.Select>
                    </UI.InputGroup>
                    <UI.InputGroup
                      sc={{
                        valid: !errors[`restrictions.${index}.min_age`],
                        touched: form.getTouched(`restrictions.${index}.min_age`),
                      }}
                    >
                      <UI.DebouncedInput
                        {...form.text(`restrictions.${index}.min_age`, { inputHandler: integerInput() })}
                        format={integerInput().format}
                        id={`min_age_${index}`}
                        maxLength={10}
                        inputMode="numeric"
                        match={/[\d]/g}
                      />
                      <UI.ErrorMessages attribute={t('min_age')} errors={errors[`restrictions.${index}.min_age`]} />
                    </UI.InputGroup>
                    <UI.InputGroup
                      sc={{
                        valid: !errors[`restrictions.${index}.max_age`],
                        touched: form.getTouched(`restrictions.${index}.max_age`),
                      }}
                    >
                      <UI.DebouncedInput
                        {...form.text(`restrictions.${index}.max_age`, { inputHandler: integerInput() })}
                        format={integerInput().format}
                        id={`max_age_${index}`}
                        maxLength={10}
                        inputMode="numeric"
                        match={/[\d]/g}
                      />
                      <UI.ErrorMessages attribute={t('max_age')} errors={errors[`restrictions.${index}.max_age`]} />
                    </UI.InputGroup>
                    <UI.Div sc={{ small: true, muted: true, pt: 1.5 }}>
                      {getDateOfBirthRange(restriction.min_age, restriction.max_age)}
                    </UI.Div>
                  </UI.FormGrid>
                  <UI.Div sc={{ pt: 0.75 }}>
                    <UI.Button
                      onClick={() => deleteRestriction(index)}
                      sc={{ blank: true, color: 'gray.400', size: 'sm' }}
                      aria-label={t('delete_restriction')}
                    >
                      <UI.Icon>
                        <Trash2 />
                      </UI.Icon>
                    </UI.Button>
                  </UI.Div>
                </UI.FormGrid>
              ))}
            </>
          )}

          <UI.Div>
            <UI.Button onClick={() => addRestriction()}>
              {t('add_restriction')}
            </UI.Button>
          </UI.Div>
        </UI.FormGrid>

        <UI.HR />

        {!ticket.business && (
          <>
            <UI.Div>
              <UI.Legend sc={{ mb: 1 }}>
                {t('multiple_ticket_assignment_options')}
              </UI.Legend>
              <UI.InputDescription>
                {t('multiple_ticket_assignment_options_description')}
              </UI.InputDescription>
            </UI.Div>

            <UI.FormGrid sc={{ gutter: 0.25 }}>
              <UI.Radio
                onChange={() => setAssignmentType(AssignmentType.required)}
                checked={form.values.assignment_type === AssignmentType.required}
                sc={{ box: true, block: true }}
              >
                <UI.Strong>{t('assignment_required_title')}</UI.Strong>
                <UI.Div>{t('assignment_required_description')}</UI.Div>
              </UI.Radio>
              <UI.Radio
                onChange={() => setAssignmentType(AssignmentType.allowed)}
                checked={form.values.assignment_type === AssignmentType.allowed}
                sc={{ box: true, block: true }}
              >
                <UI.Strong>{t('assignment_allowed_title')}</UI.Strong>
                <UI.Div>{t('assignment_allowed_description')}</UI.Div>
              </UI.Radio>
              <UI.Radio
                onChange={() => setAssignmentType(AssignmentType.afterward)}
                checked={form.values.assignment_type === AssignmentType.afterward}
                sc={{ box: true, block: true }}
              >
                <UI.Strong>{t('assignment_afterward_title')}</UI.Strong>
                <UI.Div>{t('assignment_afterward_description')}</UI.Div>
              </UI.Radio>
            </UI.FormGrid>

            <UI.HR />
          </>
        )}

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('invitation_codes')}
          </UI.Legend>

          <UI.InputDescription sc={{ mb: 2 }}>
            {t('ticket_invitations_description')}
          </UI.InputDescription>

          <UI.Paragraph>
            {ticket.invitations.length === 0 && (
              <UI.Span sc={{ muted: true }}>{t('no_invitation_codes_for_ticket')}</UI.Span>
            )}

            {ticket.invitations.map((invitation, index) => (
              <UI.Span sc={{ ml: index > 0 ? 1 : 0 }} key={index}>
                <UI.Label>
                  <UI.StatusIndicator
                    sc={{ background: invitation.usable ? 'success' : 'gray.300', mr: 1 }}
                  />
                  <UI.Strong>{invitation.prefix}</UI.Strong>
                  {invitation.has_unique_codes && (
                    <UI.Span>
                      &middot;&middot;&middot;&middot;&middot;&middot;&middot;&middot;
                    </UI.Span>
                  )}
                </UI.Label>
              </UI.Span>
            ))}
          </UI.Paragraph>

          <UI.LinkButton
            to={`/${project.id}/events/${event.id}/invitation-codes`}
            sc={{ brand: 'gray.200' }}
          >
            {t('manage_invitation_codes')}
          </UI.LinkButton>
        </UI.Div>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_ticket')}
          closeTo={`/${project.id}/events/${event.id}/tickets`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditTicketRestrictionsForm;
